/**
 * Toast Component - Non-intrusive notification system
 * 
 * Features:
 * - Success, Error, Warning, Info variants
 * - Auto-dismiss with configurable duration
 * - Manual dismiss option
 * - Stacking support (multiple toasts)
 * - Accessibility (ARIA live regions)
 * - Theme-aware styling
 * - Slide-in animations
 * 
 * Usage:
 * ```jsx
 * import { useToast } from '../contexts/ToastContext';
 * 
 * const MyComponent = () => {
 *   const { showToast } = useToast();
 *   
 *   const handleSuccess = () => {
 *     showToast({
 *       type: 'success',
 *       message: 'Settings saved successfully!',
 *       duration: 3000
 *     });
 *   };
 *   
 *   return <button onClick={handleSuccess}>Save</button>;
 * };
 * ```
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import styles from './Toast.module.css';

// Toast Types
export const ToastTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Toast Icons
// Toast Icons
const ToastIcons = {
  success: <CheckCircle size={20} />,
  error: <XCircle size={20} />,
  warning: <AlertTriangle size={20} />,
  info: <Info size={20} />
};

/**
 * Individual Toast Component
 */
const Toast = ({ id, type = ToastTypes.INFO, message, duration = 5000, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300); // Match animation duration
  };

  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const toastClasses = [
    styles.toast,
    styles[type],
    isExiting ? styles.exiting : ''
  ].filter(Boolean).join(' ');

  // ARIA role based on type
  const ariaRole = type === ToastTypes.ERROR ? 'alert' : 'status';

  return (
    <div
      className={toastClasses}
      role={ariaRole}
      aria-live={type === ToastTypes.ERROR ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div className={styles.toastContent}>
        <span className={styles.toastIcon} aria-hidden="true">
          {ToastIcons[type]}
        </span>
        <span className={styles.toastMessage}>{message}</span>
      </div>
      <button
        className={styles.toastClose}
        onClick={handleDismiss}
        aria-label="Dismiss notification"
        type="button"
      >
        <X size={16} />
      </button>
    </div>
  );
};

/**
 * Toast Container Component
 * Manages multiple toasts with stacking
 */
export const ToastContainer = ({ toasts, onDismiss, position = 'top-right' }) => {
  if (!toasts || toasts.length === 0) return null;

  const containerClasses = [
    styles.toastContainer,
    styles[`position-${position}`]
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} aria-live="polite" aria-relevant="additions">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};

export default Toast;
